import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { getActionableError } from "@/lib/errors";
import { scoreToRating, scoreToTone } from "@/lib/scoreGrade";
import { groupCustomersAlphabetically } from "@/lib/relationshipAlphabetGroups";
import {
  RELATIONSHIP_SORT,
  isAlphabeticalRelationshipSort,
  sortRelationshipCustomers,
} from "@/lib/bookmarkSorting";
import Nav from "@/components/Nav";
import RelationshipBookmark from "@/components/RelationshipBookmark";
import BookmarkSortMenu from "@/components/BookmarkSortMenu";
import {
  ArrowClockwise,
  ArrowRight,
  Buildings,
  ClockCounterClockwise,
  MagnifyingGlass,
  Phone,
  Plus,
  Users,
  X,
} from "@phosphor-icons/react";

function formatDate(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function relationshipScoreTone(value) {
  return scoreToTone(value);
}

function RelationshipScoreBadge({ value }) {
  const numericValue = Number(value);
  const hasScore = Number.isFinite(numericValue);
  const score = hasScore ? Math.max(0, Math.min(100, Math.round(numericValue))) : null;
  const tone = hasScore ? relationshipScoreTone(score) : "empty";
  const rating = hasScore ? scoreToRating(score) : "—";
  const label = hasScore ? `Relationship score ${rating}` : "Relationship score not available";

  return (
    <span
      className={`customer-card__score customer-card__score--${tone}`}
      aria-label={label}
      title={label}
    >
      {rating}
    </span>
  );
}

function CustomerCard({ customer, index }) {
  return (
    <Link
      key={customer.key}
      to={`/customers/${encodeURIComponent(customer.key)}`}
      state={{ from: "customers" }}
      data-testid={`customer-card-${customer.key}`}
      className="customer-card"
      style={{ animationDelay: `${Math.min(index, 8) * 45}ms` }}
      aria-label={`Open timeline for ${customer.name}`}
    >
      <RelationshipScoreBadge value={customer.relationship_score} />

      <span className="customer-card__content">
        <span className="customer-card__name">{customer.name}</span>
        <span className="customer-card__meta">
          {customer.company && (
            <span>
              <Buildings size={13} />
              {customer.company}
            </span>
          )}
          <span>
            <Phone size={13} />
            {customer.call_count} call{customer.call_count === 1 ? "" : "s"}
          </span>
        </span>
      </span>

      <span className="customer-card__bookmark-slot">
        <RelationshipBookmark bookmark={customer.relationship_bookmark} />
      </span>

      <span className="customer-card__dates">
        <span>
          <small>First call</small>
          {formatDate(customer.first_call_at)}
        </span>
        <span>
          <small>Latest call</small>
          {formatDate(customer.last_call_at)}
        </span>
      </span>

      <span className="customer-card__open">
        Open timeline
        <ArrowRight size={14} weight="bold" />
      </span>
    </Link>
  );
}

export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [query, setQuery] = useState(() => sessionStorage.getItem("callsect:customers:query") || "");
  const [sortMode, setSortMode] = useState(() => {
    const stored = sessionStorage.getItem("callsect:customers:sort");
    return Object.values(RELATIONSHIP_SORT).includes(stored) ? stored : RELATIONSHIP_SORT.NAME_ASC;
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const { data } = await api.get("/customers");
      setCustomers(Array.isArray(data) ? data : []);
    } catch (error) {
      setLoadError(getActionableError(error, "Callsect could not load your relationship library."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  useEffect(() => {
    sessionStorage.setItem("callsect:customers:query", query);
  }, [query]);

  useEffect(() => {
    sessionStorage.setItem("callsect:customers:sort", sortMode);
  }, [sortMode]);

  useEffect(() => {
    const saveScroll = () => {
      sessionStorage.setItem("callsect:customers:scroll", String(window.scrollY));
    };
    window.addEventListener("scroll", saveScroll, { passive: true });
    return () => {
      saveScroll();
      window.removeEventListener("scroll", saveScroll);
    };
  }, []);

  useEffect(() => {
    if (loading) return undefined;
    const savedScroll = Number(sessionStorage.getItem("callsect:customers:scroll") || 0);
    const frame = window.requestAnimationFrame(() => window.scrollTo({ top: savedScroll, left: 0 }));
    return () => window.cancelAnimationFrame(frame);
  }, [loading]);

  const normalizedQuery = query.trim().toLowerCase();
  const filteredCustomers = useMemo(() => customers.filter((customer) => (
    !normalizedQuery
    || (customer.name || "").toLowerCase().includes(normalizedQuery)
    || (customer.company || "").toLowerCase().includes(normalizedQuery)
  )), [customers, normalizedQuery]);
  const sortedCustomers = useMemo(
    () => sortRelationshipCustomers(filteredCustomers, sortMode),
    [filteredCustomers, sortMode],
  );
  const alphabeticalSort = isAlphabeticalRelationshipSort(sortMode);
  const groupedCustomers = useMemo(
    () => alphabeticalSort
      ? groupCustomersAlphabetically(sortedCustomers, sortMode === RELATIONSHIP_SORT.NAME_DESC ? "desc" : "asc")
      : [],
    [alphabeticalSort, sortMode, sortedCustomers],
  );
  return (
    <div className="customers-page">
      <Nav />

      <main className="customers-shell product-page-enter">
                <header className="customers-hero">
          <div className="customers-hero__copy">
            <div className="brand-eyebrow">YOUR CUSTOMERS</div>
            <h1>Customers</h1>
            <p>
              Reopen the full history before the next conversation: what changed, what was promised, and where the relationship stands now.
            </p>

          </div>

          <Link to="/upload" className="customers-primary-button">
            <Plus size={15} weight="bold" />
            Add conversation
          </Link>
        </header>

        <section className="customers-directory">
          <div className="customers-directory__toolbar">
            {!loading && customers.length > 0 && (
              <div className="customers-directory__controls">
                <label className="customers-search">
                  <MagnifyingGlass size={16} aria-hidden="true" />
                  <input
                    type="search"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search customers or companies..."
                    aria-label="Search customers or companies"
                    data-testid="customer-search-input"
                  />
                  {query && (
                    <button type="button" onClick={() => setQuery("")} aria-label="Clear customer search">
                      <X size={14} weight="bold" />
                    </button>
                  )}
                  <span className="customers-search__count">
                    {filteredCustomers.length}/{customers.length}
                  </span>
                </label>

                <BookmarkSortMenu
                  testId="relationship-sort"
                  className="bookmark-sort-menu__trigger--relationship"
                  summary={{
                    [RELATIONSHIP_SORT.NAME_ASC]: "Name: A → Z",
                    [RELATIONSHIP_SORT.NAME_DESC]: "Name: Z → A",
                    [RELATIONSHIP_SORT.SCORE_DESC]: "Score: High → Low",
                    [RELATIONSHIP_SORT.SCORE_ASC]: "Score: Low → High",
                    [RELATIONSHIP_SORT.LATEST_DESC]: "Call: New → Old",
                    [RELATIONSHIP_SORT.LATEST_ASC]: "Call: Old → New",
                  }[sortMode] || "Name: A → Z"}
                  ariaLabel="Sort relationships"
                  groups={[
                    {
                      label: "Name",
                      value: sortMode,
                      onValueChange: setSortMode,
                      options: [
                        { value: RELATIONSHIP_SORT.NAME_ASC, label: "A → Z", testId: "relationship-sort-name-asc" },
                        { value: RELATIONSHIP_SORT.NAME_DESC, label: "Z → A", testId: "relationship-sort-name-desc" },
                      ],
                    },
                    {
                      label: "Score",
                      value: sortMode,
                      onValueChange: setSortMode,
                      options: [
                        { value: RELATIONSHIP_SORT.SCORE_DESC, label: "High → Low", testId: "relationship-sort-score-desc" },
                        { value: RELATIONSHIP_SORT.SCORE_ASC, label: "Low → High", testId: "relationship-sort-score-asc" },
                      ],
                    },
                    {
                      label: "Call",
                      value: sortMode,
                      onValueChange: setSortMode,
                      options: [
                        { value: RELATIONSHIP_SORT.LATEST_DESC, label: "New → Old", testId: "relationship-sort-call-desc" },
                        { value: RELATIONSHIP_SORT.LATEST_ASC, label: "Old → New", testId: "relationship-sort-call-asc" },
                      ],
                    },
                  ]}
                />
              </div>
            )}
          </div>

          <div className="segmented-divider" />

          {loading ? (
            <div className="customers-loading" aria-label="Loading customers">
              {[0, 1, 2, 3].map((item) => (
                <div className="customers-skeleton" key={item}>
                  <div className="customers-skeleton__mark" />
                  <div className="customers-skeleton__copy">
                    <i />
                    <i />
                  </div>
                </div>
              ))}
            </div>
          ) : loadError ? (
            <div className="customers-empty customers-empty--error" role="alert">
              <div className="customers-empty__icon"><ArrowClockwise size={25} weight="duotone" /></div>
              <strong>Your relationships could not be loaded</strong>
              <p>{loadError}</p>
              <button type="button" className="customers-secondary-button" onClick={loadCustomers}>
                <ArrowClockwise size={15} weight="bold" />
                Try again
              </button>
            </div>
          ) : customers.length === 0 ? (
            <div className="customers-empty customers-empty--activation">
              <div className="customers-empty__icon">
                <Users size={26} weight="duotone" />
              </div>
              <strong>Your relationship library starts with the first call.</strong>
              <p>
                Add a customer or company name during upload. Future calls with the same name will extend the timeline automatically.
              </p>
              <Link to="/upload" className="customers-primary-button customers-primary-button--compact">
                <Plus size={15} weight="bold" />
                Upload the first call
              </Link>
            </div>
          ) : filteredCustomers.length === 0 ? (
            <div className="customers-empty customers-empty--compact">
              <div className="customers-empty__icon">
                <MagnifyingGlass size={24} />
              </div>
              <strong>No matching relationships</strong>
              <p>Try a different customer or company name.</p>
              <button type="button" className="customers-secondary-button" onClick={() => setQuery("")}>
                Clear search
              </button>
            </div>
          ) : (
            <div className={`customers-list${alphabeticalSort ? " customers-list--grouped" : " customers-list--sorted"}`}>
              {alphabeticalSort ? (
                groupedCustomers.map((group) => (
                  <section
                    key={group.key}
                    className="customers-alpha-group"
                    aria-labelledby={`customers-alpha-group-${group.key === "#" ? "other" : group.key}`}
                  >
                    <div className="customers-alpha-group__header">
                      <span id={`customers-alpha-group-${group.key === "#" ? "other" : group.key}`}>
                        {group.label}
                      </span>
                      <span aria-hidden="true" />
                    </div>

                    <div className="customers-alpha-group__rows">
                      {group.items.map(({ customer, index }) => (
                        <CustomerCard key={customer.key} customer={customer} index={index} />
                      ))}
                    </div>
                  </section>
                ))
              ) : (
                <div className="customers-alpha-group__rows customers-alpha-group__rows--flat">
                  {sortedCustomers.map((customer, index) => (
                    <CustomerCard key={customer.key} customer={customer} index={index} />
                  ))}
                </div>
              )}
            </div>
          )}
        </section>

        {!loading && !loadError && customers.length > 0 && (
          <div className="customers-footnote">
            <ClockCounterClockwise size={14} weight="duotone" />
            Timelines update automatically whenever a new call is analyzed.
          </div>
        )}
      </main>
    </div>
  );
}
